import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Divider, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  UploadFileOutlined as UploadIcon,
  DescriptionOutlined as DocIcon,
  FolderOutlined as FolderIcon,
} from '@mui/icons-material';
import DocumentService, {
  DocumentOwnerType, EntityFile, EntityFileRow,
} from '../../api/services/document.service';
import {
  CoverageMeter, ExpiryLine, GeneratedName,
} from '../../components/files/vaultUI';
import { colorForTone, labelForObligation, toneForObligation } from '../../components/files/vaultStatus';
import FilesShell, { FileDocumentButton, useFileUpload } from '../../components/files/FilesShell';
import PageHeader from '../../components/ui/PageHeader';
import { LoadingState } from '../../components/ui/States';
import { usePageBreadcrumb } from '../../components/layout/Breadcrumb';
import { downloadBlob } from '../../utils/download';

// One driver's or one truck's requirements checklist — including rows for
// documents that do not exist yet, which is the entire point. A list of
// what has been filed can never tell you what is missing.

const EntityFilePage: React.FC = () => {
  const navigate = useNavigate();
  const { ownerType, ownerId } = useParams<{ ownerType: string; ownerId: string }>();
  const kind = (ownerType || 'company') as DocumentOwnerType;

  const [file, setFile] = useState<EntityFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFile(await DocumentService.getEntityFile(kind, ownerId || 'company'));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load this file');
    } finally {
      setLoading(false);
    }
  }, [kind, ownerId]);

  useEffect(() => { load(); }, [load]);

  // Naming this drawer is harder than it looks. The entity endpoint returns
  // rows and a score, not a name, and the obvious trick — read the owner
  // label off a filed document — fails for exactly the person you most
  // need to see: a new hire with nothing on file yet, whose page then
  // reads "File". So the roster is the source, with the filed document as
  // a fallback for an owner the roster no longer lists (a retired truck).
  const [ownerLabel, setOwnerLabel] = useState<string | null>(null);

  useEffect(() => {
    if (kind !== 'driver' && kind !== 'vehicle') { setOwnerLabel(null); return; }
    let active = true;
    DocumentService.getRoster(kind, true)
      .then((rows) => {
        if (!active) return;
        setOwnerLabel(rows.find((row) => row.ownerId === ownerId)?.label ?? null);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [kind, ownerId]);

  // The owner's real name, or null while it is still unknown. Kept apart
  // from the display label: "File" is fine as a heading for a moment, but
  // as the upload's ownerLabel it would be baked into the generated
  // filename ("MEC_File_…"). Until the name is known the dialog falls back
  // to its owner picker instead of a locked owner.
  const ownerName = ownerLabel
    || file?.rows.find((row) => row.document?.ownerLabel)?.document?.ownerLabel
    || null;
  // What this person may file, so a row only offers filing when the server
  // would accept it. Fetched once; a failure just means no row offers it.
  const [fileable, setFileable] = useState<Set<string>>(new Set());
  useEffect(() => {
    let active = true;
    DocumentService.getUploadableTypes()
      .then((types) => { if (active) setFileable(new Set(types.map((t) => t.key))); })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);

  const label = ownerName || (kind === 'company' ? 'Company documents' : 'File');
  const uploadOwner = kind !== 'company' && ownerName
    ? { ownerType: kind, ownerId: ownerId ?? null, label: ownerName }
    : undefined;

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await DocumentService.exportZip({
        scope: 'entity', ownerType: kind, ownerId: ownerId === 'company' ? undefined : ownerId,
      });
      downloadBlob(blob, `${label.replace(/\s+/g, '-').toLowerCase()}-documents.zip`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not build that export');
    } finally {
      setExporting(false);
    }
  };

  // Required and not on file — the actual worklist for this person.
  const missingRows = (file?.rows ?? []).filter(
    (row) => row.requirement === 'required' && (row.status === 'missing' || !row.hasDocument),
  );

  const parentPath = kind === 'company' ? '/files' : `/files/roster/${kind}`;
  const parentLabel = kind === 'vehicle' ? 'Vehicles' : kind === 'driver' ? 'Drivers & staff' : 'Files';
  usePageBreadcrumb(
    kind === 'company'
      ? [{ label }]
      : [{ label: parentLabel, to: parentPath }, { label }],
  );

  return (
    <FilesShell
      onUploaded={load}
      uploadOwner={uploadOwner}
      inspector={
        <Box sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>{label}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {kind === 'vehicle' ? 'Vehicle file' : kind === 'driver' ? 'Personnel file' : 'Company file'}
          </Typography>
          <CoverageMeter score={file?.score ?? null} label="Required on file" />
          {missingRows.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="overline" color="text.disabled" display="block" sx={{ mb: 1 }}>
                Still needed
              </Typography>
              <Stack gap={0.75}>
                {missingRows.slice(0, 8).map((row) => (
                  <Typography key={row.documentTypeKey} variant="body2" color="text.secondary">
                    · {row.label}
                  </Typography>
                ))}
                {missingRows.length > 8 && (
                  <Typography variant="caption" color="text.disabled">
                    +{missingRows.length - 8} more
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      }
    
      header={
        <PageHeader
          title={label}
          actions={
            <>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={exporting}>
                {exporting ? 'Preparing…' : 'Export'}
              </Button>
              <FileDocumentButton />
            </>
          }
        />
      }
    >
      {/* Where you are, always. A file drawer you reached by clicking
          through three screens needs a way back that is not the browser
          button. */}

      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

      {loading ? (
        <LoadingState label="Loading this file…" />
      ) : (
        <>
          <Card sx={{ my: 3, p: 2 }}>
            <CoverageMeter score={file?.score ?? null} label="Required paperwork on file" />
          </Card>

          <Card sx={{ overflow: 'hidden' }}>
            {(file?.rows ?? []).map((row, index) => (
              <React.Fragment key={row.documentTypeKey}>
                {index > 0 && <Divider />}
                <RowView
                  row={row}
                  canFile={fileable.has(row.documentTypeKey)}
                  onOpen={row.folder && row.folderKey
                    ? () => navigate(`/files/folder/${row.folderKey}`)
                    : row.document ? () => navigate(`/files/document/${row.document!._id}`) : undefined}
                />
              </React.Fragment>
            ))}
          </Card>
        </>
      )}
    </FilesShell>
  );
};

const RowView: React.FC<{ row: EntityFileRow; canFile: boolean; onOpen?: () => void }> = ({
  row, canFile, onOpen,
}) => {
  const openUpload = useFileUpload();
  // A restricted row that exists shows THAT it exists and nothing else —
  // no filename, no link. hasDocument is deliberately separate from
  // document for exactly this case.
  const locked = row.hasDocument && !row.document;
  // A restricted folder still takes new files; it is the names that stay hidden.
  const fileThis = canFile && (row.folder || !locked) ? () => openUpload({ typeKey: row.documentTypeKey }) : undefined;
  // A filed row opens its document; an empty one is filed by clicking it.
  // The checklist IS the worklist — going through the generic dialog and
  // picking the type again for each of eleven rows was the complaint.
  const onClick = onOpen ?? (row.hasDocument ? undefined : fileThis);

  // The row is a name, its file, and what to do — nothing else. A status
  // pill, a "Required" tag on every line and a lock badge each told you
  // something true, but together they were a paragraph per row. The state
  // now lives on the icon (a coloured dot, or an empty dashed slot when
  // nothing is on file), the date line says when, and every row that
  // needs work has the same button. The word itself is still there for a
  // screen reader and on hover.
  const label = labelForObligation(row.status);
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2, py: 1.5, minHeight: 60,
        display: 'flex', alignItems: 'center',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" gap={2} sx={{ width: '100%' }}>
        <Tooltip title={label} placement="left">
          <Box role="img" aria-label={label} sx={{ display: 'flex', flexShrink: 0 }}>
            {row.folder
              ? <FolderGlyph />
              : row.hasDocument
                ? <StatusGlyph color={colorForTone(toneForObligation(row.status))} />
                : <EmptySlot />}
          </Box>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" gap={1} alignItems="baseline" flexWrap="wrap">
            <Typography variant="body2" fontWeight={600}>{row.label}</Typography>
            {/* Required is the norm; only the exception is worth a word. */}
            {row.requirement !== 'required' && (
              <Typography variant="caption" color="text.disabled">Optional</Typography>
            )}
          </Stack>
          {row.folder ? (
            <Typography variant="caption" color="text.secondary">
              {row.count ? `${row.count} ${row.count === 1 ? 'file' : 'files'}` : 'Empty'}
              {row.document && ` · latest ${row.document.fileName}`}
            </Typography>
          ) : row.document ? (
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <GeneratedName name={row.document.fileName} />
              <ExpiryLine expiresAt={row.document.expiresAt} />
            </Stack>
          ) : locked ? (
            <Typography variant="caption" color="text.secondary">
              On file — you do not have access to open it
            </Typography>
          ) : null}
        </Box>
        {/* A folder always takes another file; a document only until it is on file. */}
        {fileThis && (row.folder || row.status !== 'satisfied') && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            onClick={(e) => {
              e.stopPropagation();
              fileThis();
            }}
            sx={{ flexShrink: 0, width: 112 }}
          >
            {row.folder ? 'Add' : 'Upload'}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

// The state IS the icon: one plain document mark on a tile tinted by
// status — green current, amber expiring, red expired. A red PDF glyph
// with a 9px dot in its corner read as "a PDF", whatever the dot said.
const StatusGlyph: React.FC<{ color: string }> = ({ color }) => {
  const tone = color === 'inherit' ? null : color;
  return (
    <Box
      sx={{
        width: 32, height: 32, borderRadius: '8px',
        display: 'grid', placeItems: 'center',
        bgcolor: tone ? `${tone}.light` : 'grey.100',
        color: tone ? `${tone}.main` : 'text.secondary',
      }}
    >
      <DocIcon sx={{ fontSize: 18 }} />
    </Box>
  );
};

// A folder: the same tile, holding a folder mark rather than a status.
const FolderGlyph: React.FC = () => (
  <Box
    sx={{
      width: 32, height: 32, borderRadius: '8px',
      display: 'grid', placeItems: 'center', bgcolor: 'grey.100', color: 'text.secondary',
    }}
  >
    <FolderIcon sx={{ fontSize: 18 }} />
  </Box>
);

// Where a document will go: the same tile, empty and dashed.
const EmptySlot: React.FC = () => (
  <Box
    sx={{
      width: 32, height: 32, borderRadius: '8px',
      border: '1.5px dashed', borderColor: 'error.main', opacity: 0.5,
    }}
  />
);

export default EntityFilePage;
